import * as Blockly from 'blockly-tradais';
import {
  ForgeStep, Expression,
  QueryConditionItem, QueryCondition, QueryOrCondition, QueryAndCondition,
  QueryAaobFilter, QueryAaobLabelExists, QueryOrder,
  EntityReference, AsyncGroup,
} from './forge.types';
import { foldInOrder } from './condition-fold';

const SETTER_BLOCK_TYPES = new Set([
  'set_variable', // unified type-driven (replaces the scalar set_*_variable below)
  'set_string_variable', 'set_number_variable', 'set_boolean_variable',
  'set_time_stamp_variable', 'set_entity_variable', 'set_entity_list_variable',
]);

const GETTER_BLOCK_TYPES = new Set([
  'get_variable', // unified type-driven (replaces the scalar get_*_variable below)
  'get_string_variable', 'get_number_variable', 'get_boolean_variable',
  'get_time_stamp_variable', 'get_entity_variable', 'get_entity_list_variable',
]);

const AOB_GETTER_SUFFIX_TO_TYPE: Record<string, string> = {
  'string': 'String',
  'number': 'Number',
  'boolean': 'Boolean',
  'time_stamp': 'TimeStamp',
};

// === Entry Point ===

export function decompileWorkspace(
  workspace: Blockly.Workspace,
  functionName: string
): ForgeStep[] {
  const allBlocks = workspace.getTopBlocks(true);
  const funcBlock: any = allBlocks.find(
    (b: any) => b.getFieldValue('NAME') === functionName
  );
  if (!funcBlock) return [];

  const firstBlock = funcBlock.getInput('STACK')?.connection?.targetBlock();
  if (!firstBlock) return [];

  return followChain(firstBlock);
}

// === Chain Walker ===

function followChain(block: any): ForgeStep[] {
  const steps: ForgeStep[] = [];
  let current = block;
  let pendingConditions: QueryConditionItem[] = []; // imperative add_condition statements awaiting their query
  let pendingOrders: QueryOrder[] = [];             // imperative add_order statements awaiting their query
  while (current) {
    // Phase 3: conditions / orders are statements emitted before get_query — collect the run, fold back.
    if (current.type === 'add_condition' || current.type === 'condition_group') {
      const leaves: ConditionLeaf[] = [];
      while (current && (current.type === 'add_condition' || current.type === 'condition_group')) {
        leaves.push(readConditionItem(current));
        current = current.nextConnection?.targetBlock();
      }
      pendingConditions.push(...foldConditionRun(leaves));
      continue;
    }
    if (current.type === 'add_order') {
      while (current && current.type === 'add_order') {
        pendingOrders.push(readAddOrder(current));
        current = current.nextConnection?.targetBlock();
      }
      continue;
    }
    if (current.type === 'clear_conditions' || current.type === 'clear_orders') { // per-run buffer reset
      current = current.nextConnection?.targetBlock();
      continue;
    }
    const step = decompileBlock(current);
    if (step) {
      const act = (step as any).action;
      if (pendingConditions.length && (act === 'QUERY' || act === 'AGGREGATE_QUERY')) {
        (step as any).conditions = [...pendingConditions, ...((step as any).conditions || [])];
        pendingConditions = [];
      }
      if (pendingOrders.length && act === 'QUERY') {
        const existing = (step as any).orderBy;
        const existingArr = existing ? (Array.isArray(existing) ? existing : [existing]) : [];
        (step as any).orderBy = [...pendingOrders, ...existingArr];
        pendingOrders = [];
      }
      steps.push(step);
    }
    current = current.nextConnection?.targetBlock();
  }

  // TRAILING BUFFER (2026-08-05). add_condition / add_order are statements that BUFFER above and fold
  // into the next QUERY. If the chain ends before a query arrives, the loop exits with the buffer still
  // full and every one of those blocks is discarded — the user wrote steps and the round trip deleted
  // them, with no error and no sentinel. The estate has zero instances of this today (empty-plan triage,
  // 2026-08-05: bucket C is 0/552), so this is insurance, not a repair — but the imperative condition
  // builder is new and Forge is about to become the main producer of these blocks, which is exactly when
  // an orphaned buffer becomes reachable. Made LOUD rather than silent, following the same 2026-07-26
  // decision that replaced the decompiler's silent drop with an UNSUPPORTED sentinel: there is no step
  // that can hold a condition with no query, so the honest output is a marker a backfill can find.
  if (pendingConditions.length || pendingOrders.length) {
    steps.push({
      action: 'UNSUPPORTED',
      blockType: pendingConditions.length ? 'add_condition (no query followed)' : 'add_order (no query followed)',
    } as ForgeStep);
  }
  return steps;
}

function decompileStatementBody(block: any, inputName: string): ForgeStep[] {
  const first = block.getInput(inputName)?.connection?.targetBlock();
  return first ? followChain(first) : [];
}

// === Block Decompiler ===

function decompileBlock(block: any): ForgeStep | null {
  const type: string = block.type;

  if (SETTER_BLOCK_TYPES.has(type)) return decompileSetterBlock(block);

  if (type === 'set_reference_entity_variable_for_AOB') return decompileSetAobRef(block);
  if (type === 'set_aob_variable' || type.startsWith('set_reference_entity_variable_for_AOB_variable_')) return decompileSetAobField(block);
  if (type === 'set_key_value_in_AOB_reference') return decompileSetAaobKeyValue(block);

  switch (type) {
    case 'controls_if': return decompileIf(block);
    case 'custom_loop': return decompileLoopRange(block);
    case 'controls_forEach': return decompileLoopForEach(block);
    case 'controls_repeat_ext': return decompileRepeat(block);
    case 'controls_for': return decompileForLoopAsRange(block);
    case 'controls_whileUntil': return decompileWhileUntil(block);
    case 'create_entity': return decompileCreateEntity(block);
    case 'update_entity':
    case 'update_entity_by_id': return decompileUpdateEntity(block);
    case 'push_new_element_to_array': return decompilePushToArray(block);
    case 'push_to_entity_list': return { action: 'PUSH_TO_ENTITY_LIST', target: block.getFieldValue('VAR') };
    case 'delete_from_entity_list':
    case 'delete_entity': return decompileDeleteFromList(block);
    case 'text_append': return decompileTextAppend(block);
    case 'async_function_wrapper': return decompileAsync(block);
    case 'resolve_promise_by_group': return decompileResolveAsync(block);
    case 'show_toast': return decompileToast(block);
    case 'set_navigation': return { action: 'NAVIGATE', page: block.getFieldValue('VAR') || '' };
    case 'open_external_string':
    case 'open_external_link': return { action: 'OPEN_EXTERNAL', url: exprFrom(block, 'URL') };
    case 'show_loading_screen':
    case 'set_loading': return decompileLoading(block);
    // BOTH return blocks decompile to the SAME lane-agnostic RETURN step. That is deliberate: the step
    // model describes what the function does, not which palette it came from. Which block comes back on
    // recompile is decided by ctx.lane in steps-to-blockly, not recorded here.
    case 'return_boolean':
    case 'return_value': return decompileReturn(block);
    case 'controls_flow_statements':
      return block.getFieldValue('FLOW') === 'CONTINUE'
        ? { action: 'CONTINUE' } : { action: 'BREAK' };
    case 'get_current_user': return { action: 'GET_CURRENT_USER', destination: valVar(block, 'user') };
    case 'get_users_list': return { action: 'GET_USERS_LIST', destination: valVar(block, 'users') };
    case 'get_roles_list': return { action: 'GET_ROLES_LIST', destination: valVar(block, 'roles') };
    case 'create_user': return { action: 'CREATE_USER' };
    case 'edit_user': return { action: 'EDIT_USER', entityVar: valVar(block, 'VARIABLE') };
    case 'logout_user':
    case 'logout': return { action: 'LOGOUT' };
    case 'time_difference': return decompileTimeDiff(block);
    case 'check_location_access': return decompileCheckLocationAccess(block);
    case 'ask_location_access': return { action: 'ASK_LOCATION_ACCESS' };
    default:
      if (type.endsWith('_api_functions')) return decompileCallDynamic(block, 'CALL_FUNCTION', '_api_functions');
      if (type.endsWith('_ap_workflow')) return decompileCallDynamic(block, 'CALL_WORKFLOW', '_ap_workflow');
      // LOUD fallback (2026-07-26): don't silently drop an unrecognized statement block (unknown or legacy,
      // e.g. deprecated `entity_conditions`). Emit a visible sentinel so a backfill can flag the function.
      return { action: 'UNSUPPORTED', blockType: type };
  }
}

/**
 * Read an index slot (2026-08-05). Every index reader used to be some variant of
 * `idxBlock?.getFieldValue('VAR') || <fallback>`, which is wrong twice over:
 *
 *  1. A CALCULATED index was thrown away. `getFieldValue('VAR')` is null on a `math_arithmetic`, so the
 *     fallback fired and the whole expression became 0 / "0" / "". `length - 1` — "the last item" —
 *     decompiled as index 0, the FIRST item. Wrong answer, no error. 501 stored blocks do this.
 *  2. `getFieldValue('VAR')` matches ANY block carrying a VAR field, not just a variable getter.
 *     `get_entity_length` carries one, so a bare `entity_length(list)` index read back as the LIST
 *     variable itself rather than its length.
 *
 * Both are fixed by asking the real question — is this a variable getter? — and decompiling anything
 * else as the expression it is. GETTER_BLOCK_TYPES is the same set `decompileExpression` uses at its own
 * var branch, so "variable" means the same thing here as everywhere else.
 *
 * Returning a bare string for the getter case is deliberate and load-bearing: both compilers branch on
 * `typeof step.indexVar === 'string'` to decide variable-vs-expression, and both already compile the
 * expression form (`forge-compiler.ts:438`, `steps-to-blockly.ts:272`). The write half has always
 * supported this; only the read half discarded it.
 */
function readIndex(idxBlock: any, fallback: string | number): string | number | Expression {
  if (!idxBlock) return fallback;
  if (GETTER_BLOCK_TYPES.has(idxBlock.type)) return idxBlock.getFieldValue('VAR');
  return decompileExpression(idxBlock);
}

// === Setter Handler (detects compound patterns) ===

function decompileSetterBlock(block: any): ForgeStep | null {
  const type: string = block.type;
  const target = block.getFieldValue('VAR');
  if (!target) return null;

  const valueBlock = vBlock(block, 'VALUE');

  if (type === 'set_entity_list_variable' && valueBlock?.type === 'get_query') {
    return decompileQuery(target, valueBlock);
  }
  if ((type === 'set_number_variable' || type === 'set_variable') && valueBlock?.type === 'entity_aggregate_query') {
    return decompileAggregateQuery(target, valueBlock);
  }
  if ((type === 'set_number_variable' || type === 'set_variable') && valueBlock?.type === 'aaob_aggregate_by_label') {
    return decompileAaobAggregateQuery(target, valueBlock);
  }
  if (type === 'set_entity_variable' && valueBlock?.type === 'set_entity_variable_by_index_of_entity_list') {
    const listBlock = vBlock(valueBlock, 'VALUE');
    const idxBlock = vBlock(valueBlock, 'INDEX');
    return {
      action: 'SET_ENTITY_FROM_LIST',
      target,
      listVar: listBlock?.getFieldValue('VAR') || '',
      indexVar: readIndex(idxBlock, 0),
    };
  }

  return { action: 'SET', target, value: valueBlock ? decompileExpression(valueBlock) : 0 };
}

// === Compound Step Decompilers ===

function decompileQuery(destination: string, queryBlock: any): ForgeStep {
  const step: any = { action: 'QUERY', entity: queryBlock.getFieldValue('VAR'), destination };

  const orderBlock = vBlock(queryBlock, 'B');
  if (orderBlock?.type === 'aaob_sort_by_label') {
    step.aaobOrderBy = {
      aaobField: orderBlock.getFieldValue('VAR'),
      // VERBATIM (2026-08-04, D3). This collapsed the DIR dropdown's six Hasura values to asc/desc, so a
      // saved `asc_nulls_last` decompiled as `asc` — and since these steps RECOMPILE to the live
      // `add_order`, decompile+recompile silently moved every null to the other end of the result. A
      // behaviour change wearing a format change's clothes. `direction` is typed `string` on both sides
      // and add_order's DIR accepts all six, so passing it through needs nothing else. The live
      // `readAddOrder` path never narrowed — this was the deprecated block's alone.
      direction: orderBlock.getFieldValue('DIR') || 'asc',
      valueType: orderBlock.getFieldValue('TYPE') || 'string',
      key: exprFrom(orderBlock, 'KEY'),
    };
  } else if (orderBlock?.type === 'get_set_order_variable') {
    const raw = orderBlock.getFieldValue('VAR') || '';
    const i = raw.indexOf('$');
    step.orderBy = {
      field: i >= 0 ? raw.substring(0, i) : raw,
      direction: orderBlock.getFieldValue('DROPDOWN') === 'desc' ? 'desc' as const : 'asc' as const,
    };
  }

  const limitBlock = vBlock(queryBlock, 'C');
  if (limitBlock) step.limit = decompileExpression(limitBlock);

  const offsetBlock = vBlock(queryBlock, 'D');
  if (offsetBlock) step.offset = decompileExpression(offsetBlock);

  const condBlock = vBlock(queryBlock, 'CONDITIONS');
  if (condBlock) step.conditions = decompileConditionChain(condBlock);

  // Deprecated: childEntities replaced by imperative conditions
  // const childBlock = vBlock(queryBlock, 'EC');
  // if (childBlock) step.childEntities = decompileChildEntities(childBlock);

  return step;
}

function decompileAggregateQuery(destination: string, aggBlock: any): ForgeStep {
  const columnRaw = aggBlock.getFieldValue('COLUMN') || '';
  const i = columnRaw.indexOf('$');
  const step: any = {
    action: 'AGGREGATE_QUERY',
    entity: aggBlock.getFieldValue('VAR'),
    op: aggBlock.getFieldValue('OP') || 'count',
    destination,
  };
  if (columnRaw) step.column = i >= 0 ? columnRaw.substring(0, i) : columnRaw;

  const condBlock = vBlock(aggBlock, 'CONDITIONS');
  if (condBlock) step.conditions = decompileConditionChain(condBlock);

  return step;
}

function decompileAaobAggregateQuery(destination: string, aggBlock: any): ForgeStep {
  const step: any = {
    action: 'AAOB_AGGREGATE_QUERY',
    entity: aggBlock.getFieldValue('VAR'),
    aaobField: aggBlock.getFieldValue('AAOB_FIELD'),
    op: aggBlock.getFieldValue('OP') || 'count',
    key: exprFrom(aggBlock, 'KEY'),
    destination,
  };

  const condBlock = vBlock(aggBlock, 'CONDITIONS');
  if (condBlock) step.conditions = decompileConditionChain(condBlock);

  // Deprecated: childEntities replaced by imperative conditions
  // const childBlock = vBlock(aggBlock, 'EC');
  // if (childBlock) step.childEntities = decompileChildEntities(childBlock);

  return step;
}

// === Individual Step Decompilers ===

function decompileIf(block: any): ForgeStep {
  const branches: { condition: Expression; then: ForgeStep[] }[] = [];
  const count = (block.elseifCount_ ?? 0) + 1;

  for (let i = 0; i < count; i++) {
    branches.push({
      condition: exprFrom(block, `IF${i}`),
      then: decompileStatementBody(block, `DO${i}`),
    });
  }

  const step: any = { action: 'IF', branches };
  if (block.elseCount_ > 0) {
    const elseBody = decompileStatementBody(block, 'ELSE');
    if (elseBody.length > 0) step.else = elseBody;
  }
  return step;
}

function decompileLoopRange(block: any): ForgeStep {
  const varBlock = vBlock(block, 'VARIABLE');
  return {
    action: 'LOOP_RANGE',
    from: exprFrom(block, 'FROM') ?? 0,
    to: exprFrom(block, 'TO') ?? 0,
    step: exprFrom(block, 'STEP'),
    indexVar: varBlock?.getFieldValue('VAR') || String(varBlock?.getFieldValue('NUM') ?? 0),
    body: decompileStatementBody(block, 'DO'),
  };
}

function decompileLoopForEach(block: any): ForgeStep {
  return {
    action: 'LOOP_FOREACH',
    itemVar: block.getFieldValue('VAR'),
    list: exprFrom(block, 'LIST'),
    body: decompileStatementBody(block, 'DO'),
  };
}

function decompileRepeat(block: any): ForgeStep {
  return {
    action: 'REPEAT',
    times: exprFrom(block, 'TIMES'),
    body: decompileStatementBody(block, 'DO'),
  };
}

function decompileForLoopAsRange(block: any): ForgeStep {
  const step: any = {
    action: 'LOOP_RANGE',
    indexVar: block.getFieldValue('VAR'),
    from: exprFrom(block, 'FROM'),
    to: exprFrom(block, 'TO'),
    body: decompileStatementBody(block, 'DO'),
  };
  const byBlock = vBlock(block, 'BY');
  if (byBlock) step.step = decompileExpression(byBlock);
  return step;
}

function decompileWhileUntil(block: any): ForgeStep {
  return {
    action: 'WHILE_UNTIL',
    mode: (block.getFieldValue('MODE') === 'UNTIL' ? 'UNTIL' : 'WHILE') as 'WHILE' | 'UNTIL',
    condition: exprFrom(block, 'BOOL'),
    body: decompileStatementBody(block, 'DO'),
  };
}

function decompileCreateEntity(block: any): ForgeStep {
  const step: any = { action: 'CREATE_ENTITY', entity: block.getFieldValue('VAR') };
  const entityInput = vBlock(block, 'ENTITY');
  if (entityInput) step.destination = entityInput.getFieldValue('VAR');
  const refInput = vBlock(block, 'VALUE');
  if (refInput) step.entityReferences = decompileEntityRefChain(refInput);
  const flagInput = vBlock(block, 'FLAG');
  if (flagInput) step.successFlag = flagInput.getFieldValue('VAR');
  return step;
}

function decompileUpdateEntity(block: any): ForgeStep {
  const entityInput = vBlock(block, 'VALUE');
  const step: any = { action: 'UPDATE_ENTITY', entityVar: entityInput?.getFieldValue('VAR') || '' };
  const refInput = vBlock(block, 'ENTITY_REFERENCE_SELECTION');
  if (refInput) step.entityReferences = decompileEntityRefChain(refInput);
  return step;
}

function decompileSetAobRef(block: any): ForgeStep {
  const target = block.getFieldValue('VAR');
  const inner = vBlock(block, 'VALUE');
  if (inner?.type === 'get_reference_entity_variable_for_AOB_reference_AOB') {
    const idxBlock = vBlock(inner, 'VALUE');
    return {
      action: 'SET_AOB_REF',
      target,
      listVar: inner.getFieldValue('VAR'),
      indexVar: readIndex(idxBlock, ''),
    };
  }
  return { action: 'SET_AOB_REF', target, listVar: '', indexVar: '' };
}

function decompileSetAobField(block: any): ForgeStep {
  return {
    action: 'SET_AOB_FIELD',
    target: block.getFieldValue('VAR'),
    value: exprFrom(block, 'VALUE') ?? 0,
  };
}

function decompileSetAaobKeyValue(block: any): ForgeStep {
  // AAOB_FIELD + TYPE added 2026-08-04. They were on the block from the start and dropped here, so a
  // decompiled upsert could not be rebuilt: the generator only emits the upsert call when AAOB_FIELD is
  // non-empty, and TYPE is half the (systemKey, systemType) match key. `NANAAOB_FIELD` is the
  // unconfigured sentinel — mirrors entity-variable-healper.ts's generator.
  const rawAaob = block.getFieldValue('AAOB_FIELD') || '';
  return {
    action: 'SET_AAOB_KEY_VALUE',
    target: block.getFieldValue('VAR'),
    aaobField: rawAaob === 'NANAAOB_FIELD' ? '' : rawAaob,
    valueType: block.getFieldValue('TYPE') || 'String',
    key: exprFrom(block, 'KEY') ?? '',
    value: exprFrom(block, 'VALUE') ?? '',
  };
}

function decompilePushToArray(block: any): ForgeStep {
  const arrInput = vBlock(block, 'ARRAY_INPUT');
  const step: any = { action: 'PUSH_TO_ARRAY', target: arrInput?.getFieldValue('VAR') || '' };

  // Read optional index
  const idxBlock = vBlock(block, 'INDEX_INPUT');
  if (idxBlock) step.index = decompileExpression(idxBlock);

  // Read per-field values from V2 mutator fieldRows_
  const rows = block.fieldRows_ || [];
  if (rows.length > 0) {
    const fields: Record<string, Expression> = {};
    for (const r of rows) {
      const valBlock = vBlock(block, r.inputName);
      if (valBlock) fields[r.key] = decompileExpression(valBlock);
    }
    if (Object.keys(fields).length > 0) step.fields = fields;
  }

  return step;
}

function decompileDeleteFromList(block: any): ForgeStep {
  const arrInput = vBlock(block, 'ARRAY_INPUT');
  const idxInput = vBlock(block, 'INDEX');
  return {
    action: 'DELETE_FROM_LIST',
    target: arrInput?.getFieldValue('VAR') || '',
    // this one had no literal branch at all, so even `delete item 3` lost the 3
    indexVar: readIndex(idxInput, ''),
  };
}

function decompileTextAppend(block: any): ForgeStep {
  return {
    action: 'TEXT_APPEND',
    target: block.getFieldValue('VAR'),
    text: exprFrom(block, 'TEXT'),
  };
}

function decompileCallDynamic(block: any, action: 'CALL_FUNCTION' | 'CALL_WORKFLOW', suffix: string): ForgeStep {
  const name = (block.type as string).replace(new RegExp(suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$'), '');
  const inputs: Record<string, Expression> = {};
  const outputs: Record<string, string> = {};

  for (const input of block.inputList) {
    if (input.type !== 1) continue;
    const child = input.connection?.targetBlock();
    if (!child) continue;
    if (GETTER_BLOCK_TYPES.has(child.type)) {
      outputs[input.name] = child.getFieldValue('VAR');
    } else {
      inputs[input.name] = decompileExpression(child);
    }
  }

  const step: any = { action, name, inputs };
  if (Object.keys(outputs).length > 0) step.outputs = outputs;
  return step;
}

function decompileTimeDiff(block: any): ForgeStep {
  // Same input/output split as decompileCallDynamic: a value slot holding a variable GETTER is an
  // output (assigned into); anything else is an input Expression. Plus the UNIT dropdown.
  const inputs: Record<string, Expression> = {};
  const outputs: Record<string, string> = {};
  for (const input of block.inputList) {
    if (input.type !== 1) continue; // value inputs only
    const child = input.connection?.targetBlock();
    if (!child) continue;
    if (GETTER_BLOCK_TYPES.has(child.type)) {
      outputs[input.name] = child.getFieldValue('VAR');
    } else {
      inputs[input.name] = decompileExpression(child);
    }
  }
  const step: any = { action: 'TIME_DIFF', unit: block.getFieldValue('UNIT') || 'day', inputs };
  if (block.getFieldValue('ZONE') === 'utc') step.zone = 'utc';
  if (Object.keys(outputs).length > 0) step.outputs = outputs;
  return step;
}

/**
 * check_location_access — same output-slot shape as decompileTimeDiff, minus the inputs: no value
 * slot here reads an expression, only a getter-or-empty. An unconnected or non-getter slot is simply
 * skipped, exactly like time_difference.
 */
function decompileCheckLocationAccess(block: any): ForgeStep {
  const outputs: Record<string, string> = {};
  for (const input of block.inputList) {
    if (input.type !== 1) continue; // value inputs only
    const child = input.connection?.targetBlock();
    if (!child) continue;
    if (GETTER_BLOCK_TYPES.has(child.type)) {
      outputs[input.name] = child.getFieldValue('VAR');
    }
  }
  const step: any = { action: 'CHECK_LOCATION_ACCESS' };
  if (Object.keys(outputs).length > 0) step.outputs = outputs;
  return step;
}

function decompileAsync(block: any): ForgeStep {
  const raw = block.getFieldValue('ASYNC_GROUP') || '';
  const group = raw.replace('async_group_', '') as AsyncGroup;
  const step: any = { action: 'ASYNC', group, try: decompileStatementBody(block, 'TRY') };
  const catchBody = decompileStatementBody(block, 'CATCH');
  if (catchBody.length > 0) step.catch = catchBody;
  const finallyBody = decompileStatementBody(block, 'FINALLY');
  if (finallyBody.length > 0) step.finally = finallyBody;
  return step;
}

function decompileResolveAsync(block: any): ForgeStep {
  const raw = block.getFieldValue('ASYNC_GROUP') || '';
  return {
    action: 'RESOLVE_ASYNC',
    group: raw.replace('async_group_', '') as AsyncGroup,
    body: decompileStatementBody(block, 'BODY'),
  };
}

function decompileToast(block: any): ForgeStep {
  const step: any = {
    action: 'TOAST',
    message: exprFrom(block, 'MESSAGE') ?? '',
    type: block.getFieldValue('TOAST_TYPE') || 'success',
  };
  const durBlock = vBlock(block, 'DURATION');
  if (durBlock) {
    const dur = decompileExpression(durBlock);
    if (typeof dur === 'number' && dur !== 3000) step.duration = dur;
  }
  return step;
}

function decompileLoading(block: any): ForgeStep {
  const showBlock = vBlock(block, 'showLoading');
  return {
    action: 'LOADING',
    show: showBlock?.type === 'logic_boolean' ? showBlock.getFieldValue('BOOL') === 'TRUE' : true,
  };
}

function decompileReturn(block: any): ForgeStep {
  const v = vBlock(block, 'VALUE');
  const step: any = { action: 'RETURN' };
  if (v) step.value = decompileExpression(v);
  return step;
}

// === Expression Decompiler ===

function decompileExpression(block: any): Expression {
  if (!block) return null;
  const type: string = block.type;

  if (type === 'math_number') return Number(block.getFieldValue('NUM')) || 0;
  if (type === 'text') return String(block.getFieldValue('TEXT') ?? '');
  if (type === 'logic_boolean') return block.getFieldValue('BOOL') === 'TRUE';
  if (type === 'logic_null') return null;

  if (GETTER_BLOCK_TYPES.has(type)) return { var: block.getFieldValue('VAR') };
  if (type === 'get_reference_entity_variable_for_AOB_reference_AOB') return { var: block.getFieldValue('VAR') };

  if (type === 'get_entity_length') return { fn: 'entity_length', args: [{ var: block.getFieldValue('VAR') }] };
  if (type === 'get_email') return { fn: 'get_email', args: [] };

  // ── auth / current-user VALUE blocks (special-functions-helper.ts) — output-typed, so they arrive as
  //    expressions plugged into a set/return. Mirror their generator arg order. (2026-07-26 coverage fill.) ──
  if (type === 'get_current_user_role') return { fn: 'get_current_user_role', args: [] };
  if (type === 'auth_login') return { fn: 'auth_login', args: [exprFrom(block, 'EMAIL'), exprFrom(block, 'PASSWORD')] };
  // landing page "send password reset link" (2026-09-25) — Boolean VALUE block, one input.
  if (type === 'auth_send_password_reset') return { fn: 'auth_send_password_reset', args: [exprFrom(block, 'EMAIL')] };
  if (type === 'auth_signup_found') return { fn: 'auth_signup_found', args: [exprFrom(block, 'EMAIL'), exprFrom(block, 'PASSWORD'), exprFrom(block, 'USERNAME'), exprFrom(block, 'TENANTNAME')] };
  if (type === 'tenant_add_user') return { fn: 'tenant_add_user', args: [exprFrom(block, 'EMAIL'), exprFrom(block, 'USERNAME'), exprFrom(block, 'ROLE')] };
  if (type === 'tenant_cancel_invite') return { fn: 'tenant_cancel_invite', args: [exprFrom(block, 'EMAIL')] };
  if (type === 'tenant_edit_user_role') return { fn: 'tenant_edit_user_role', args: [exprFrom(block, 'EMAIL'), exprFrom(block, 'USERNAME'), exprFrom(block, 'ROLE')] };
  if (type === 'user_create_account') return { fn: 'user_create_account', args: [block.getFieldValue('MODE') || 'email', exprFrom(block, 'VALUE'), exprFrom(block, 'PASSWORD'), exprFrom(block, 'ROLE')] };
  // People blocks (Plan 3, 2026-10-03). Round-trip so an AI rebuild keeps one a builder placed — they are
  // NOT in the AI's vocabulary (Plan 4). Colours ride as three trailing strings; Transfer's ticked entity
  // ids as one comma-joined string.
  if (type === 'person_remove' || type === 'person_reinstate' || type === 'person_erase') {
    return { fn: type, args: [exprFrom(block, 'PERSON'), ...peopleThemeArgs(block)] };
  }
  if (type === 'user_delete_account') return { fn: 'user_delete_account', args: peopleThemeArgs(block) };
  if (type === 'person_transfer') {
    return { fn: 'person_transfer', args: [exprFrom(block, 'FROM'), exprFrom(block, 'TO'), (block.chosenEntities_?.() || []).join(','), ...peopleThemeArgs(block)] };
  }
  // delete record (Plan, 2026-10-06). Same round-trip reasoning as the people blocks: an AI rebuild keeps
  // one a builder placed. ASK rides as the literal string 'TRUE'/'FALSE', like the checkbox field itself.
  if (type === 'delete_record') {
    return { fn: 'record_delete', args: [exprFrom(block, 'RECORD'), block.getFieldValue('ASK') === 'FALSE' ? 'FALSE' : 'TRUE'] };
  }
  // Facebook page leads on/off (B4, 2026-10-09). Same round-trip reasoning as delete_record.
  if (type === 'meta_page_subscription') {
    return { fn: 'meta_page_subscription', args: [exprFrom(block, 'PAGE'), block.getFieldValue('ACTION') === 'subscribe' ? 'subscribe' : 'unsubscribe'] };
  }
  if (type === 'user_set_email') return { fn: 'user_set_email', args: [exprFrom(block, 'IDENTIFIER'), exprFrom(block, 'EMAIL')] };
  if (type === 'user_set_username') return { fn: 'user_set_username', args: [exprFrom(block, 'IDENTIFIER'), exprFrom(block, 'USERNAME')] };
  if (type === 'user_reset_password') return { fn: 'user_reset_password', args: [exprFrom(block, 'IDENTIFIER'), exprFrom(block, 'NEW_PASSWORD')] };
  if (type === 'user_change_password') return { fn: 'user_change_password', args: [exprFrom(block, 'CURRENT'), exprFrom(block, 'NEW')] };
  if (type === 'app_version') return { fn: 'app_version', args: [] };

  if (type === 'math_arithmetic' || type === 'logic_compare' || type === 'logic_operation') {
    return { op: block.getFieldValue('OP'), left: exprFrom(block, 'A'), right: exprFrom(block, 'B') };
  }
  if (type === 'math_modulo') {
    return { op: 'MODULO' as any, left: exprFrom(block, 'DIVIDEND'), right: exprFrom(block, 'DIVISOR') };
  }

  if (type === 'logic_negate') return { op: 'NOT' as any, arg: exprFrom(block, 'BOOL') };
  if (type === 'math_single') return { op: block.getFieldValue('OP'), arg: exprFrom(block, 'NUM') };
  if (type === 'math_round') return { op: block.getFieldValue('OP'), arg: exprFrom(block, 'NUM') };

  if (type === 'math_trig') return { fn: 'math_trig', args: [exprFrom(block, 'NUM'), block.getFieldValue('OP')] };
  if (type === 'math_constant') return { fn: 'math_constant', args: [block.getFieldValue('CONSTANT')] };
  if (type === 'math_number_property') return { fn: 'math_number_property', args: [exprFrom(block, 'NUMBER_TO_CHECK'), block.getFieldValue('PROPERTY')] };
  if (type === 'math_on_list') return { fn: 'math_on_list', args: [exprFrom(block, 'LIST'), block.getFieldValue('OP')] };
  if (type === 'math_random_float') return { fn: 'math_random_float', args: [] };
  if (type === 'math_constrain') return { fn: 'math_constrain', args: [exprFrom(block, 'VALUE'), exprFrom(block, 'LOW'), exprFrom(block, 'HIGH')] };
  if (type === 'math_random_int') return { fn: 'math_random_int', args: [exprFrom(block, 'FROM'), exprFrom(block, 'TO')] };

  if (type === 'text_join' || type === 'lists_create_with') {
    const args: Expression[] = [];
    const count = block.itemCount_ ?? 100;
    for (let i = 0; i < count; i++) {
      const inp = block.getInput(`ADD${i}`);
      if (!inp) break;
      const child = inp.connection?.targetBlock();
      args.push(child ? decompileExpression(child) : (type === 'text_join' ? '' : null));
    }
    return { fn: type === 'text_join' ? 'text_join' : 'lists_create_with', args };
  }

  if (type === 'text_length') return { fn: 'text_length', args: [exprFrom(block, 'VALUE')] };
  if (type === 'text_isEmpty') return { fn: 'text_isEmpty', args: [exprFrom(block, 'VALUE')] };
  // END (2026-08-05, L2). This read VALUE and FIND only, so the END dropdown — FIRST vs LAST, i.e.
  // indexOf vs lastIndexOf — never reached the step, and BOTH compilers then hardcoded 'FIRST'
  // (forge-compiler `fields:[{END:'FIRST'}]`, steps-to-blockly `mk(…,{END:'FIRST'})`). A saved "find the
  // LAST occurrence" round-tripped into "find the FIRST occurrence": a different answer, silently.
  // L1 could not see it because the field NAME always came back — the compiler emitted it, just wrong.
  // Threaded as args[2] exactly like text_charAt's WHERE / text_changeCase's CASE / text_trim's MODE,
  // which all already do this. Old steps have no args[2] and fall back to 'FIRST', the prior behaviour.
  if (type === 'text_indexOf') return { fn: 'text_indexOf', args: [exprFrom(block, 'VALUE'), exprFrom(block, 'FIND'), block.getFieldValue('END')] };
  if (type === 'text_charAt') return { fn: 'text_charAt', args: [exprFrom(block, 'VALUE'), exprFrom(block, 'AT'), block.getFieldValue('WHERE')] };
  // WHERE1/WHERE2 (2026-08-05, L2 follow-up). Same family as text_indexOf above, one step worse: these
  // two dropdowns were read by NOTHING and emitted by NEITHER compiler, so a saved "from letter 2 to the
  // LAST letter" recompiled as "…to letter ⟨nothing⟩". Invisible to L1 *and* L2 because the compilers
  // emit this block with no `fields` at all, and both scans skip a node without a fields array.
  // These are not plain pass-throughs: they drive the block's shape. Blockly's own mutation
  // (`<mutation at1="true" at2="false"/>`) removes the AT input a FIRST/LAST setting makes meaningless,
  // so args[3]/args[4] must be applied as FIELDS and the AT connections left to follow — which they do,
  // because setFieldValue fires the validator that reshapes the block before any input is connected.
  if (type === 'text_getSubstring') return { fn: 'text_getSubstring', args: [exprFrom(block, 'STRING'), exprFrom(block, 'AT1'), exprFrom(block, 'AT2'), block.getFieldValue('WHERE1'), block.getFieldValue('WHERE2')] };
  if (type === 'text_changeCase') return { fn: 'text_changeCase', args: [exprFrom(block, 'TEXT'), block.getFieldValue('CASE')] };
  if (type === 'text_trim') return { fn: 'text_trim', args: [exprFrom(block, 'TEXT'), block.getFieldValue('MODE')] };
  if (type === 'text_prompt_ext') return { fn: 'text_prompt_ext', args: [exprFrom(block, 'TEXT'), block.getFieldValue('TYPE')] };
  if (type === 'value_is_empty') return { fn: 'value_is_empty', args: [exprFrom(block, 'VALUE')] };
  if (type === 'string_contains') return { fn: 'string_contains', args: [exprFrom(block, 'TEXT'), exprFrom(block, 'SUBSTRING')] };
  if (type === 'string_regex_match') return { fn: 'string_regex_match', args: [exprFrom(block, 'TEXT'), exprFrom(block, 'REGEX')] };
  if (type === 'polygon_area') return { fn: 'polygon_area', args: [exprFrom(block, 'GEOMETRY'), block.getFieldValue('UNIT')] };
  if (type === 'reverse_geocode') return { fn: 'reverse_geocode', args: [exprFrom(block, 'GEOMETRY')] };
  if (type === 'point_count') return { fn: 'point_count', args: [exprFrom(block, 'GEOMETRY')] };

  if (type === 'format_iso_date') return { fn: 'format_iso_date', args: [exprFrom(block, 'DATE'), exprFrom(block, 'FORMAT'), ...utcTail(block)] };
  if (type === 'parse_date_to_iso') return { fn: 'parse_date_to_iso', args: [exprFrom(block, 'VALUE'), block.getFieldValue('FORMAT') || 'YYYY-MM-DD', ...utcTail(block)] }; // FORMAT is a dropdown field now
  if (type === 'current_date_iso') return { fn: 'current_date_iso', args: [] };
  if (type === 'date_add_days') return { fn: 'date_add_days', args: [exprFrom(block, 'DATE'), exprFrom(block, 'DAYS'), block.getFieldValue('UNIT') || 'day', ...utcTail(block)] };
  if (type === 'set_date') return { fn: 'build_date', args: [Number(block.getFieldValue('DAY')), Number(block.getFieldValue('MONTH')), Number(block.getFieldValue('YEAR')), ...utcTail(block)] };
  if (type === 'set_time') return { fn: 'set_time_of_day', args: [exprFrom(block, 'DATE'), Number(block.getFieldValue('HOUR')), Number(block.getFieldValue('MINUTE')), ...utcTail(block)] };
  // Were __unsupported__ until 2026-09-19 — a Forge rebuild lost them outright, choice and all.
  if (type === 'date_start_of_month') return { fn: 'date_start_of_month', args: [exprFrom(block, 'DATE'), ...utcTail(block)] };
  if (type === 'date_end_of_month') return { fn: 'date_end_of_month', args: [exprFrom(block, 'DATE'), ...utcTail(block)] };
  if (type === 'date_start_of_day') return { fn: 'date_start_of_day', args: [exprFrom(block, 'DATE'), ...utcTail(block)] };
  if (type === 'date_end_of_day') return { fn: 'date_end_of_day', args: [exprFrom(block, 'DATE'), ...utcTail(block)] };
  if (type === 'date_diff_hours') return { fn: 'date_diff_hours', args: [exprFrom(block, 'DATE_A'), exprFrom(block, 'DATE_B')] };
  if (type === 'date_diff_minutes') return { fn: 'date_diff_minutes', args: [exprFrom(block, 'DATE_A'), exprFrom(block, 'DATE_B')] };

  if (type === 'trim_decimals') return { fn: 'trim_decimals', args: [exprFrom(block, 'VALUE'), exprFrom(block, 'DIGITS')] };
  if (type === 'number_to_words') return { fn: 'number_to_words', args: [exprFrom(block, 'NUM')] };

  if (type === 'logic_ternary') return { fn: 'ternary', args: [exprFrom(block, 'IF'), exprFrom(block, 'THEN'), exprFrom(block, 'ELSE')] };

  if (type === 'get_aob_variable') {
    // unified AOB getter: leaf type comes from the variable, not a block-name suffix
    const v = block.workspace?.getVariableById(block.getFieldValue('VAR'));
    return { fn: 'get_aob_field', args: [{ var: block.getFieldValue('VAR') }, (v?.type) || 'String'] };
  }
  if (type.startsWith('get_reference_entity_variable_for_AOB_variable_')) {
    const suffix = type.replace('get_reference_entity_variable_for_AOB_variable_', '');
    return { fn: 'get_aob_field', args: [{ var: block.getFieldValue('VAR') }, AOB_GETTER_SUFFIX_TO_TYPE[suffix] || 'String'] };
  }

  // LOUD fallback (2026-07-26): an unrecognized VALUE block (unknown, or the not-yet-handled index-getters
  // get_function_variable_at_index / get_function_entity_at_index) becomes a visible sentinel, not a silent
  // null — so a backfill can detect the lossy value instead of trusting a hole. `fn` is generic string.
  return { fn: '__unsupported__', args: [type] };
}

/**
 * A day block's "in UTC" switch as Forge sees it (2026-09-19): a trailing 'utc' argument. Project time —
 * the default — emits NOTHING, so every step Forge has seen stays byte-identical and a step without it
 * means project time.
 */
function utcTail(block: any): ['utc'] | [] {
  return block.getFieldValue('ZONE') === 'utc' ? ['utc'] : [];
}

function exprFrom(block: any, inputName: string): Expression {
  const child = vBlock(block, inputName);
  return child ? decompileExpression(child) : null;
}

/** A people block's three colour knobs, in order: accent, surface, text. */
function peopleThemeArgs(block: any): string[] {
  return ['ACCENT', 'SURFACE', 'TEXT'].map((n) => String(block.getFieldValue(n) || ''));
}

// === Condition Decompiler ===

// === Imperative condition decompiler (Phase 3.4) — read add_condition/condition_group statements ===

interface ConditionLeaf { item: QueryConditionItem; connector: 'AND' | 'OR'; }

function readConditionItem(block: any): ConditionLeaf {
  const connField = block.getFieldValue('CONNECTOR');
  const isDynConn = connField === '__dynamic__';
  const connector: 'AND' | 'OR' = isDynConn ? 'AND' : (connField === 'OR' ? 'OR' : 'AND');
  if (block.type === 'condition_group') {
    const leaves: ConditionLeaf[] = [];
    let b = block.getInput('BODY')?.connection?.targetBlock();
    while (b && (b.type === 'add_condition' || b.type === 'condition_group')) {
      leaves.push(readConditionItem(b));
      b = b.nextConnection?.targetBlock();
    }
    return { item: foldGroupBody(leaves), connector };
  }
  const cond = readAddCondition(block);
  if (isDynConn) cond.connector = exprFrom(block, 'DYNCONN');
  return { item: cond, connector };
}

export function readAddCondition(block: any): QueryCondition {
  let d: any = {};
  try { d = JSON.parse(block.data || '{}'); } catch { /* unconfigured */ }
  const op = block.getFieldValue('OP');
  const cond: any = {
    field: d.source === 'aaob' ? (d.aaobField || '') : (Array.isArray(d.path) ? d.path.join('.') : ''),
  };
  if (d.onPath) cond.on = d.onPath;
  if (op === '__dynamic__') {
    cond.op = exprFrom(block, 'DYNOP');
    cond.value = exprFrom(block, 'VALUE');
    // typed between (2026-10-09): the "to" socket travels when something is plugged in.
    if (block.getInputTargetBlock && block.getInputTargetBlock('VALUETO')) cond.valueTo = exprFrom(block, 'VALUETO');
  } else if (op === '_isnull_true') { cond.op = '_is_null'; cond.value = true; }
  else if (op === '_isnull_false') { cond.op = '_is_null'; cond.value = false; }
  else if (op === '_between') { cond.op = op; cond.value = exprFrom(block, 'VALUE'); cond.valueTo = exprFrom(block, 'VALUETO'); }
  else { cond.op = op; cond.value = exprFrom(block, 'VALUE'); }
  if (d.source === 'aaob') {
    cond.aaobKey = exprFrom(block, 'KEY');
    if (d.dataType) cond.aaobType = d.dataType;
  }
  // Missing value: only 'skip' travels as a step. 'false' is the default and 'legacy' is never written, so a
  // Forge modify re-decides it (zavo_Sep_01, grilling Q8).
  if (block.getFieldValue('ONMISSING') === 'skip') cond.ifMissing = 'skip';
  // Whole days (2026-10-09): only where the block offers it — a Date & time field, an operator that takes a value.
  if (block.getFieldValue('WHOLEDAYS') === 'TRUE' && d.source !== 'aaob' && d.dataType === 'TimeStamp'
    && op !== '_isnull_true' && op !== '_isnull_false') cond.wholeDays = true;
  return cond as QueryCondition;
}

function readAddOrder(block: any): QueryOrder {
  let d: any = {};
  try { d = JSON.parse(block.data || '{}'); } catch { /* unconfigured */ }
  const dir = block.getFieldValue('DIR') || 'asc';
  if (d.source === 'aaob') {
    return { on: d.onPath || undefined, field: d.aaobField || '', direction: dir, aaobKey: exprFrom(block, 'KEY'), aaobType: d.dataType || 'String' };
  }
  return { on: d.onPath || undefined, field: Array.isArray(d.path) ? d.path.join('.') : '', direction: dir };
}

function splitConditionRunByOr(leaves: ConditionLeaf[]): ConditionLeaf[][] {
  const runs: ConditionLeaf[][] = [[]];
  leaves.forEach((l, i) => {
    if (i > 0 && l.connector === 'OR') runs.push([]);
    runs[runs.length - 1].push(l);
  });
  return runs;
}

// Was (until 2026-10-06): split into OR-runs — & before ||.
// `A, || B, & C` read as { or: [[A], [B, C]] } = A or (B and C). Conditions now combine in the order written
// (ADR 0011), so it is (A or B) and C — foldInOrder (condition-fold.ts) does the reading.
// function foldConditionRun(leaves: ConditionLeaf[]): QueryConditionItem[] {
//   if (!leaves.length) return [];
//   const runs = splitConditionRunByOr(leaves);
//   if (runs.length === 1) return runs[0].map(l => l.item); // implicit-AND list (mixed items OK)
//   return [{ or: runs.map(run => run.map(l => l.item as QueryCondition)) }];
// }
// function foldGroupBody(leaves: ConditionLeaf[]): QueryConditionItem {
//   const runs = splitConditionRunByOr(leaves);
//   if (runs.length > 1) return { or: runs.map(run => run.map(l => l.item as QueryCondition)) };
//   return { and: [leaves.map(l => l.item)] };
// }

/** Top-level run → declarative conditions list, read in the order written (ADR 0011). */
function foldConditionRun(leaves: ConditionLeaf[]): QueryConditionItem[] {
  if (!leaves.length) return [];
  return foldInOrder(leaves);
}

/** A condition_group's body → one QueryConditionItem: an OR if it folds to one, else a bracket. */
function foldGroupBody(leaves: ConditionLeaf[]): QueryConditionItem {
  const items = foldInOrder(leaves);
  if (items.length === 1 && 'or' in (items[0] as any)) return items[0];
  return { and: [items] };
}

function decompileConditionChain(block: any): QueryConditionItem[] {
  const conditions: QueryConditionItem[] = [];
  let current = block;

  while (current) {
    const type: string = current.type;

    if (type === 'entity_or_conditions') {
      conditions.push(decompileOrCondition(current));
      current = null;
    } else if (type === 'entity_and_conditions') {
      conditions.push(decompileAndCondition(current));
      current = null;
    } else if (type === 'aaob_filter_by_label') {
      conditions.push({
        aaobField: current.getFieldValue('VAR'),
        op: current.getFieldValue('OP') || '_eq',
        valueType: current.getFieldValue('TYPE') || 'string',
        key: exprFrom(current, 'KEY'),
        value: exprFrom(current, 'VALUE'),
      } as QueryAaobFilter);
      current = vBlock(current, 'SIBILING_CONDITIONS');
    } else if (type === 'aaob_label_exists') {
      conditions.push({
        aaobField: current.getFieldValue('VAR'),
        key: exprFrom(current, 'KEY'),
      } as QueryAaobLabelExists);
      current = vBlock(current, 'SIBILING_CONDITIONS');
    } else if (type.startsWith('entity_set_') && type.endsWith('_in_conditions')) {
      conditions.push(decompileSimpleCondition(current));
      current = vBlock(current, 'SIBILING_CONDITIONS');
    } else {
      current = null;
    }
  }

  return conditions;
}

function decompileSimpleCondition(block: any): QueryCondition {
  const varId = block.getFieldValue('VAR') || '';
  const i = varId.indexOf('$');
  const field = i >= 0 ? varId.substring(0, i) : varId;

  const setBlock = vBlock(block, 'SET');

  if (setBlock?.type === 'entity_is_null_conditions') {
    return { field, op: '_is_null', value: setBlock.getFieldValue('DROPDOWN') === 'true' };
  }

  const op = setBlock?.getFieldValue('DROPDOWN') || '_eq';
  const valueBlock = setBlock ? vBlock(setBlock, 'SET') : null;
  return { field, op, value: valueBlock ? decompileExpression(valueBlock) : null };
}

function decompileOrCondition(block: any): QueryOrCondition {
  const branches: QueryCondition[][] = [];
  (function collect(b: any) {
    const child = vBlock(b, 'CHILD_CONDITIONS');
    const sibling = vBlock(b, 'SIBILING_CONDITIONS');
    if (child?.type === 'entity_or_conditions') collect(child);
    else if (child) branches.push(decompileConditionChain(child) as QueryCondition[]);
    if (sibling) branches.push(decompileConditionChain(sibling) as QueryCondition[]);
  })(block);
  return { or: branches };
}

function decompileAndCondition(block: any): QueryAndCondition {
  const branches: QueryConditionItem[][] = [];
  (function collect(b: any) {
    const child = vBlock(b, 'CHILD_CONDITIONS');
    const sibling = vBlock(b, 'SIBILING_CONDITIONS');
    if (child?.type === 'entity_and_conditions') collect(child);
    else if (child) branches.push(decompileConditionChain(child));
    if (sibling) branches.push(decompileConditionChain(sibling));
  })(block);
  return { and: branches };
}

// === Child Entity Decompiler ===
// Deprecated: childEntities replaced by imperative conditions (add_condition on child entity target)

// function decompileChildEntities(block: any): QueryChildEntity[] {
//   const children: QueryChildEntity[] = [];
//   let current = block;
//
//   while (current?.type === 'entity_conditions') {
//     const child: any = { childEntity: current.getFieldValue('VAR'), conditions: [] };
//
//     const orderBlock = vBlock(current, 'B');
//     if (orderBlock?.type === 'get_set_order_variable') {
//       const raw = orderBlock.getFieldValue('VAR') || '';
//       const i = raw.indexOf('$');
//       child.orderBy = {
//         field: i >= 0 ? raw.substring(0, i) : raw,
//         direction: orderBlock.getFieldValue('DROPDOWN') === 'desc' ? 'desc' : 'asc',
//       };
//     }
//
//     const limitBlock = vBlock(current, 'C');
//     if (limitBlock) child.limit = decompileExpression(limitBlock);
//
//     const condBlock = vBlock(current, 'CONDITIONS');
//     if (condBlock) child.conditions = decompileConditionChain(condBlock);
//
//     children.push(child);
//     current = vBlock(current, 'E');
//   }
//
//   return children;
// }

// === Entity Reference Decompiler ===

function decompileEntityRefChain(block: any): EntityReference[] {
  const refs: EntityReference[] = [];
  let current = block;

  while (current?.type === 'entity_reference_selection') {
    const entityBlock = vBlock(current, 'ENTITY');
    refs.push({
      field: current.getFieldValue('VAR'),
      entityVar: entityBlock?.getFieldValue('VAR') || '',
      // Explicit link/build mode (from the block's CONSIDER_ENTITY dropdown). considerEntity kept for
      // back-compat readers. Under update_entity the block forces CONSIDER_ENTITY=TRUE, so update refs read as link.
      mode: current.getFieldValue('CONSIDER_ENTITY') === 'TRUE' ? 'link' : 'build',
      considerEntity: current.getFieldValue('CONSIDER_ENTITY') === 'TRUE' || undefined,
    });
    current = vBlock(current, 'NEXT');
  }

  return refs;
}

// === Helpers ===

function vBlock(block: any, inputName: string): any | null {
  return block.getInput(inputName)?.connection?.targetBlock() || null;
}

function valVar(block: any, inputName: string): string {
  return vBlock(block, inputName)?.getFieldValue('VAR') || '';
}
